// Select wrapper around Base UI's headless Select primitive.
//
// Drop-in replacement for native <select> — same value/onChange API surface.
// Visual style mirrors the .input class so it slots into existing forms.
//
// Usage:
//   <Select value={cat} onChange={setCat}>
//     <SelectOption value="all">All</SelectOption>
//     <SelectOption value="ai">AI Models</SelectOption>
//   </Select>
//
// Or for arrays:
//   <Select value={cat} onChange={setCat} options={[
//     { value: 'all', label: 'All' },
//     { value: 'ai',  label: 'AI Models' },
//   ]}/>

import React from 'react';
import { Select as BaseSelect } from '@base-ui/react/select';

const TRIGGER_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  height: 32,
  padding: '0 10px 0 12px',
  fontSize: 13,
  color: 'var(--fg-1)',
  background: 'var(--bg-1)',
  border: '1px solid var(--line-2)',
  borderRadius: 6,
  cursor: 'pointer',
  outline: 'none',
  fontFamily: 'inherit',
  // Force the selected label to render on one line and ellipsis when too long. Without these
  // the BaseSelect.Value wraps onto two rows and pushes the trigger taller — visible most
  // obviously when an item label is a long UUID.
  whiteSpace: 'nowrap',
  overflow: 'hidden',
};

const POPUP_STYLE = {
  background: 'var(--bg-1)',
  border: '1px solid var(--line-2)',
  borderRadius: 6,
  padding: 4,
  boxShadow: '0 10px 30px rgba(0,0,0,0.6)',
  fontSize: 13,
  color: 'var(--fg-1)',
  minWidth: 'var(--anchor-width)',
  zIndex: 100,
};

const ITEM_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 10px',
  borderRadius: 4,
  cursor: 'pointer',
  outline: 'none',
};

export const Select = ({
  value, onChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  width,
  children,
  triggerStyle,
  triggerClassName = '',
}) => {
  const items = options
    ? options.map(o => <SelectOption key={String(o.value)} value={o.value}>{o.label}</SelectOption>)
    : children;

  // Base UI's <Select.Value> renders the raw value string when the matching
  // Item lives inside a Portal'd Popup (the ItemText lookup doesn't reach
  // across the portal boundary in our setup). When callers pass `options`
  // we already have the label → render it ourselves; for `children`-based
  // usage we fall back to the default behavior (caller knows what they're doing).
  const selectedLabel = options
    ? (options.find(o => o.value === value)?.label ?? null)
    : null;

  return (
    <BaseSelect.Root value={value} onValueChange={(v) => onChange && onChange(v)} disabled={disabled}>
      <BaseSelect.Trigger
        className={triggerClassName}
        style={{ ...TRIGGER_STYLE, ...(width != null ? { width } : null), ...(triggerStyle || {}) }}
      >
        {/* Wrap the Value in a flexible inner span so the label truncates instead of wrapping
            (the trigger itself is inline-flex; the Value's own text node was overflowing before). */}
        <span style={{flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
          {selectedLabel != null ? (
            <span>{selectedLabel}</span>
          ) : value && options ? (
            // value provided but not in options (stale data) — show placeholder so the user
            // doesn't see a half-broken state. Better than rendering the raw enum.
            <span style={{ color: 'var(--fg-4)' }}>{placeholder}</span>
          ) : (
            <BaseSelect.Value placeholder={placeholder}/>
          )}
        </span>
        <BaseSelect.Icon style={{ marginLeft: 'auto', color: 'var(--fg-3)' }}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3.5 6l4.5 4 4.5-4"/>
          </svg>
        </BaseSelect.Icon>
      </BaseSelect.Trigger>
      <BaseSelect.Portal>
        <BaseSelect.Positioner sideOffset={4}>
          <BaseSelect.Popup style={POPUP_STYLE}>
            {items}
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  );
};

export const SelectOption = ({ value, children }) => (
  <BaseSelect.Item
    value={value}
    style={ITEM_STYLE}
    className="ui-select-item"
  >
    <BaseSelect.ItemIndicator style={{ width: 12, display: 'flex' }}>
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 8.5l3 3 7-7"/>
      </svg>
    </BaseSelect.ItemIndicator>
    <BaseSelect.ItemText>{children}</BaseSelect.ItemText>
  </BaseSelect.Item>
);
